import { lazy, useEffect } from "react";
import { Router, Switch, Route } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { RecoilRoot } from "recoil";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloProvider,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { useExperiment } from "@statsig/react-bindings";

import { Main } from "./main";
import history from "./history";
import theme from "./theme/themeThree";
import { UserStatePreFill } from "./MVVM/Hooks/userStatePreFill";
import { GlobalStyles } from "./theme/globalStyles";
import { ClarityProvider } from "./MVVM/Providers";
import { analytics } from "MVVM/Analytics";
import { GamePreferenceInitializer } from "MVVM/Providers/GamePreferenceInitializer";

const { REACT_APP_GQL_ENDPOINT } = process.env;

const LazyIframe = lazy(() => import("./MVVM/Views/Iframe"));
const LazyEmbeddablePlayer = lazy(() =>
  import("./MVVM/Views/EmbeddablePlayer"),
);

const IframeExperiment = () => {
  const experiment = useExperiment("iframe_redesign");

  return experiment.get("show_iframe_redesign", false) ? (
    <LazyEmbeddablePlayer />
  ) : (
    <LazyIframe />
  );
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      /*
      TODO:
      Add logzio logging
      */
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("token");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      authorization: token ? `Bearer ${token}` : "",
      ...headers,
    },
  };
});

const httpLink = createHttpLink({
  uri: REACT_APP_GQL_ENDPOINT,
});

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Promotions: {
        keyFields: ["name"],
      },
    },
  }),
  link: authLink.concat(errorLink).concat(httpLink),
});

export default function () {
  useEffect(() => {
    analytics.page();
  });
  return (
    <ApolloProvider client={apolloClient}>
      <RecoilRoot id="root">
        <ClarityProvider>
          <ThemeProvider theme={theme}>
            <GlobalStyles />
            <Router history={history}>
              <GamePreferenceInitializer />
              <UserStatePreFill force={false} />
              <Switch>
                <Route exact path="/iframe" component={IframeExperiment} />
                <Main />
              </Switch>
            </Router>
          </ThemeProvider>
        </ClarityProvider>
      </RecoilRoot>
    </ApolloProvider>
  );
}
